import React from 'react';
import { tx, localizeDeep } from '../utils/i18n';
import { routePath } from '../i18n/routes';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { TrendingUp, DollarSign, Target, ArrowRight, CheckCircle, Plug, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BusinessModelSection = () => {
  const { t, currentLanguage } = useLanguage();

  const getBusinessModelContent = () => {
    if (currentLanguage === 'en') {
      return {
        title: "Revolutionary Business Model",
        subtitle: "CPC to CPA - You only pay for results",
        description: "As traffic brokers, we take the risk so you don't have to. We buy traffic at CPC and you only pay at CPA.",
        wePayTitle: "We Pay",
        wePayDescription: "Cost Per Click (CPC)",
        wePayDetail: "We invest in traffic acquisition and take the performance risk",
        youPayTitle: "You Pay", 
        youPayDescription: "Cost Per Action (CPA)",
        youPayDetail: "You only pay for real conversions - sales, leads, registrations",
        benefits: [
          "Zero advertising risk for you",
          "Guaranteed ROI on every payment", 
          "Performance-based partnership",
          "Continuous traffic optimization",
          "Full campaign transparency"
        ],
        cta: "Discover our CPA model"
      };
    } else if (currentLanguage === 'de') {
      return {
        title: "Revolutionäres Geschäftsmodell",
        subtitle: "CPC zu CPA - Sie zahlen nur für Ergebnisse", 
        description: "Als Traffic-Broker übernehmen wir das Risiko, damit Sie es nicht müssen. Wir kaufen Traffic zum CPC und Sie zahlen nur zum CPA.",
        wePayTitle: "Wir zahlen",
        wePayDescription: "Cost Per Click (CPC)",
        wePayDetail: "Wir investieren in Traffic-Akquisition und übernehmen das Performance-Risiko",
        youPayTitle: "Sie zahlen",
        youPayDescription: "Cost Per Action (CPA)", 
        youPayDetail: "Sie zahlen nur für echte Conversions - Verkäufe, Leads, Registrierungen",
        benefits: [
          "Null Werberisiko für Sie",
          "Garantierter ROI bei jeder Zahlung",
          "Performance-basierte Partnerschaft", 
          "Kontinuierliche Traffic-Optimierung",
          "Vollständige Kampagnen-Transparenz"
        ],
        cta: "Entdecken Sie unser CPA-Modell"
      };
    }
    const fr = /* i18n:extract */ {
        title: "Modèle économique révolutionnaire",
        subtitle: "CPC vers CPA - Vous ne payez que les résultats",
        description: "En tant que traffic brokers, nous prenons le risque pour que vous n'ayez pas à le faire. Nous achetons le trafic au CPC et vous ne payez qu'au CPA.",
        wePayTitle: "Nous payons",
        wePayDescription: "Coût Par Clic (CPC)",
        wePayDetail: "Nous investissons dans l'acquisition de trafic et prenons le risque de performance",
        youPayTitle: "Vous payez",
        youPayDescription: "Coût Par Action (CPA)",
        youPayDetail: "Vous ne payez que pour les conversions réelles - ventes, leads, inscriptions",
        benefits: [
          "Zéro risque publicitaire pour vous",
          "ROI garanti à chaque paiement",
          "Partenariat basé sur la performance",
          "Optimisation continue du trafic",
          "Transparence totale des campagnes"
        ],
        cta: "Découvrir notre modèle CPA"
    }; /* i18n:end */
    return localizeDeep(fr, currentLanguage);
  };

  const content = getBusinessModelContent();

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-green-100 text-green-800 mb-4">
            <TrendingUp className="w-4 h-4 mr-2" />
            Traffic Brokers
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {content.title}
          </h2>
          <h3 className="text-2xl md:text-3xl bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent font-semibold mb-6">
            {content.subtitle}
          </h3>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {content.description}
          </p>
        </div>

        {/* Business Model Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* We Pay - CPC */}
          <Card className="relative overflow-hidden border-2 border-red-200 hover:shadow-xl transition-all duration-500">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{content.wePayTitle}</h3>
                <div className="text-2xl font-bold text-red-600 mb-2">{content.wePayDescription}</div>
                <p className="text-gray-600 text-sm">{content.wePayDetail}</p>
              </div>
              
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-red-800 font-medium text-sm">
                  {tx(currentLanguage, 'OptimHub prend le risque financier', 'OptimHub takes the financial risk', 'OptimHub übernimmt das finanzielle Risiko')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* You Pay - CPA */}
          <Card className="relative overflow-hidden border-2 border-green-200 hover:shadow-xl transition-all duration-500">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-blue-500"></div>
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{content.youPayTitle}</h3>
                <div className="text-2xl font-bold text-green-600 mb-2">{content.youPayDescription}</div>
                <p className="text-gray-600 text-sm">{content.youPayDetail}</p>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-green-800 font-medium text-sm">
                  {tx(currentLanguage, 'ROI garanti sur chaque euro dépensé', 'Guaranteed ROI on every euro spent', 'Garantierter ROI für jeden ausgegebenen Euro')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Publisher API access & placement transparency */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <Card className="border-2 border-cyan-200 hover:shadow-xl transition-all duration-500" data-testid="placement-transparency-card">
            <CardContent className="p-8">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {tx(currentLanguage, 'La transparence de nos placements est une priorité', 'Placement transparency is a priority', 'Placement-Transparenz ist für uns Priorität')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {tx(currentLanguage, "Nos marchands savent toujours où leurs campagnes sont diffusées : sites, emplacements et performances associées. Cette visibilité est une priorité pour nous et reste accessible tout au long de la collaboration.", 'Our merchants always know where their campaigns run: sites, placements and the performance attached to them. This visibility is a priority for us and stays available throughout the collaboration.', 'Unsere Merchants wissen jederzeit, wo ihre Kampagnen ausgeliefert werden: Websites, Placements und die zugehörige Performance. Diese Transparenz hat für uns Priorität und bleibt während der gesamten Zusammenarbeit verfügbar.')}
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-500">
            <CardContent className="p-8 flex flex-col h-full">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center mb-6">
                <Plug className="w-7 h-7 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {tx(currentLanguage, 'Éditeurs : une API unique, plusieurs plateformes de monétisation', 'Publishers: one API, several monetization platforms', 'Publisher: eine API, mehrere Monetarisierungsplattformen')}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {tx(currentLanguage, "Connectez votre site, votre application ou votre outil à OptimHub et accédez aux plateformes de monétisation disponibles via une seule intégration, en CPC comme en CPA.", 'Connect your site, app or tool to OptimHub and reach the available monetization platforms through a single integration, on CPC as well as CPA.', 'Verbinden Sie Ihre Website, App oder Ihr Tool mit OptimHub und erreichen Sie die verfügbaren Monetarisierungsplattformen über eine einzige Integration – im CPC wie im CPA.')}
                </p>
              </div>
              <a
                href={routePath('publishers', currentLanguage)}
                data-testid="business-model-publishers-link"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-200"
              >
                {tx(currentLanguage, "Demander mon accès API", 'Request API access', 'API-Zugang anfragen')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Benefits List */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {tx(currentLanguage, 'Vos avantages avec notre modèle CPA', 'Your advantages with our CPA model', 'Ihre Vorteile mit unserem CPA-Modell')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {content.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-3xl p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              {tx(currentLanguage, 'Prêt à adopter le modèle CPA sans risque ?', 'Ready to adopt the risk-free CPA model?', 'Bereit für das risikofreie CPA-Modell?')}
            </h3>
            <p className="text-lg text-green-100 mb-8 max-w-2xl mx-auto">
              {tx(currentLanguage, 'Rejoignez les entreprises qui ne paient que pour les résultats réels et maximisent leur ROI publicitaire.', 'Join companies that only pay for real results and maximize their advertising ROI.', 'Schließen Sie sich Unternehmen an, die nur für echte Ergebnisse zahlen und ihren Werbe-ROI maximieren.')}
            </p>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-green-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center"
            >
              {content.cta}
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessModelSection;